// t7-1b (T4, PLAN A): LA TARJETA DE UNA CARD DEL GRUPO EN EL PANEL DEL ALUMNO.
//
// ARCHIVO EN ASCII A PROPOSITO (regla de la casa para los `.js` nuevos).
//
// Una card es una publicacion del profe ("#k" de un documento del banco) y el
// alumno la estudia como a un documento suyo. Por eso se pinta con la MISMA tarjeta
// de un documento propio "Listo" del Panel (Anexo A 3): la pareja movil y
// escritorio de `pages/Dashboard.js`, con sus cadenas de clases copiadas LETRA POR
// LETRA (contenedor, renglon, caja del icono, nombre recortado, linea de
// informacion y chip) y la envoltura de ritmo vertical de `SalidaSuave`, para que
// una tarjeta propia y una de card, lado a lado, tengan el mismo aire.
// `TarjetaDeCard.test.js` lee los dos fuentes: si el Panel cambia sus clases, avisa.
//
// Lo que NO se copia, a proposito: el bote y toda accion de dueno, los avisos de
// procesamiento, la bodega de la app y la marca `doc-<id>` (la busca `lib/push.js`
// para llevar al alumno a SU tarjeta). Esta lleva `card-<id>` y `card-<id>-mobile`.
//
// EL HUECO DEL BOTE (revision adversarial de la tanda; DESVIACION DECLARADA de la
// copia letra por letra). Sin bote, el renglon movil de una tarjeta propia "Listo"
// mide lo que mide el bote (su caja tactil de 44) y el de la card, lo que mide la
// caja del icono (32): medido en Chrome a 375, la card salia 12 px mas baja que la
// tarjeta propia de al lado. Por eso cada gemela lleva, donde la tarjeta propia
// lleva el bote, una pieza VACIA con las utilidades de tamano del bote de esa
// gemela, copiadas de el (`HUECO_MOVIL`, `HUECO_ESCRITORIO`), escondida del lector
// de pantalla y sin nada que picar. Asi la card activa mide lo mismo de alto (medido
// a 375, 768 y 1280, ES y EN) y el nombre y la linea de informacion tienen el mismo
// ancho que en una tarjeta propia. La prueba lee el bote de `Dashboard.js` y compara
// sus utilidades de tamano con estas.
//
// LA CADUCADA (decision D17): el aspecto de la tarjeta bloqueada del Panel y SOLO
// eso, la atenuacion en el contenedor y el candado en la caja del icono. Sin chip,
// sin cursor ni tacto, y NUNCA un texto de bloqueo ni de mejorar plan. Al picarla,
// el banner del chasis con el texto 13 (`common:errors.group_material_not_available`)
// con un `id` fijo (tres toques, un banner) y sin navegar. Sin chip, su linea de
// informacion es la de un renglon de texto, mas baja que la del chip: medido, la
// caducada sale 6 px mas baja que la activa a menos de md y 4.5 desde md. No se
// rellena a proposito (D17: el aspecto de bloqueado y SOLO eso).
//
// Los textos llegan por `t` (el del Panel, que ya tiene cargados `dashboard` y
// `common`) y el idioma por `idioma`, que decide el formato de la fecha.
//
// t7-1c (P, #479): `gemela` ("movil" o "escritorio") pinta UNA sola de las dos, para
// la tarjeta del grupo, que es a su vez una pareja y lleva sus cards DENTRO de cada
// gemela: la gemela movil del grupo solo con la gemela movil de cada card, y la de
// escritorio solo con la suya, sin marcas repetidas en el DOM. Sin `gemela` (el
// valor por omision) pinta las dos, exactamente como antes.
//
// ── dashboard-rediseno (15-oct-2026): "PUBLICACION DEL GRUPO" (4.6 y bloque C) ─────────────────
// LAPIDA de lo de arriba: la card ya no copia la pareja de dos renglones de la tarjeta de un
// documento. Es la tarjeta INTERNA del Panel nuevo (el cascaron de anatomia fija de
// `components/panel/`, fondo #101010, borde #1F1F1F, radio 14, relleno 14, icono de 36): el
// titulo en dos renglones, "{n} topics", la Preparacion si existe (la misma de Analiticas) y "View
// topics". Sin menu de tres puntos (no se borra ni se renombra). C2: con fecha, el renglon 2 dice
// "Expires {date}" con su calendario. C3 (caducada): atenuada, con candado, "Expired {date}", sin
// Preparacion ni "View topics"; al tocarla, el aviso de hoy. C4 (solo app): mientras su paquete
// baja se ve exactamente como procesando (regla 6) y, sin red, como A16 ("Tap to retry").
// Lo que hace cada toque no cambia.
import React, { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Calendar, FileText, ListChecks, Lock } from 'lucide-react';
import { toast } from 'sonner';
import TemasDeLaParteModal, { VER_TEMAS_MOVIL, VER_TEMAS_ESCRITORIO } from './TemasDeLaParteModal';
import { fechaDeCaducidadDe } from '../lib/fechaDeCaducidad';
import { CAJA_DEL_ICONO_MOVIL, CAJA_DEL_ICONO_ESCRITORIO } from './IconoDeMaterial';
import CascaronDeTarjeta from './panel/CascaronDeTarjeta';
import ChipDeEstado from './panel/ChipDeEstado';
import { Medidor, MedidorDePreparacion } from './panel/Medidor';
import {
  AVISO_LARGO, RENGLON, RENGLON_DOS, BOTON_PIE_SECUNDARIO, COLOR_BARRA_PROCESANDO, TRAZO_ICONO_INTERNA, nombreConCortes,
  ICONO_VER_TEMAS,
} from './panel/anatomia';

// El cursor y el tacto de la tarjeta que se puede estudiar.
export const CURSOR_DE_LISTA = 'cursor-pointer hoverable:border-[#3A2414] active:border-[#3A2414] active:bg-[#1B1B1B] active:duration-0 transition-colors';
// LAPIDA dashboard-rediseno: el chip verde "Listo" se quito (documento de diseno, "Anatomia fija").
export const COLOR_LISTO = 'bg-emerald-500/15 text-emerald-400 border-emerald-500/20';
// La atenuacion de la caducada (icono, titulo y detalle al 50 %: la pone el cascaron).
export const ATENUADA = 'opacity-75';
// Un solo banner por mucho que se pique: sonner reemplaza el que tenga este id.
export const AVISO_ID = 'card-caducada';
// LAPIDA dashboard-rediseno: el hueco del bote de cada gemela ya no existe (no hay bote ni pareja
// de dos renglones); las cadenas se quedan exportadas para quien las leia.
export const HUECO_MOVIL = 'min-h-[44px] min-w-[44px] h-10 w-10 shrink-0';
export const HUECO_ESCRITORIO = 'h-8 w-8 shrink-0';
export { CAJA_DEL_ICONO_MOVIL, CAJA_DEL_ICONO_ESCRITORIO, VER_TEMAS_MOVIL, VER_TEMAS_ESCRITORIO };
export const COLOR_PROCESANDO = 'bg-amber-500/15 text-amber-400 border-amber-500/20';
const BAJANDO = 'downloading';
const PREPARANDO = 'preparing';
// Mientras el paquete baja, la barra se queda donde la deja un documento que baja (#412): el 5 %
// de arranque, quieta.
const PCT_BAJANDO = 5;

export default function TarjetaDeCard({
  card, t, idioma, gemela, temaCoincidente = null, entrega = null, alReintentar = null, cuantosTemas = 1, preparacion,
}) {
  const navigate = useNavigate();
  const caducada = card.estado === 'caducada';
  const conMovil = gemela !== 'escritorio';
  const conEscritorio = gemela !== 'movil';

  // t7-3 remate 5 (A3, #572): el dia que eligio el profe (`caduca_dia`), el mismo en cualquier zona.
  const fecha = fechaDeCaducidadDe(card, idioma);
  const conteo = t('dashboard:doc_card.topic_count', { count: (card.temas || []).length });
  const llaveFecha = caducada ? 'dashboard:grupos_alumno.caduco' : 'dashboard:grupos_alumno.caduca';

  // remates-1-3-0b (P3, #514): "View topics" abre la MISMA ventana con los temas que la card ya trae.
  const [temasAbiertos, setTemasAbiertos] = useState(false);
  const volverA = useRef(null);
  const temasFijos = (Array.isArray(card.temas) ? card.temas : [])
    .filter((x) => x && x.nombre)
    .map((x) => ({ name: x.nombre, question_count: x.count || 0, flashcard_count: 0 }));

  const bajando = !caducada && entrega === BAJANDO;
  const preparando = !caducada && entrega === PREPARANDO;
  const picar = () => {
    if (caducada) {
      toast.error(t('common:errors.group_material_not_available'), { id: AVISO_ID });
      return;
    }
    if ((bajando || preparando) && alReintentar) { alReintentar(card); return; }
    navigate(`/study?doc=${card.doc_id}&pub=${card.id}`);
  };
  const estado = caducada ? 'C3' : preparando ? 'A16' : bajando ? 'C4' : fecha ? 'C2' : 'C';

  const detalle = (sufijo) => {
    if (preparando) {
      return (
        <p className={`${AVISO_LARGO} text-[#9A9A9A]`} data-testid={`preparing-notice-card-${card.id}${sufijo === '-mobile' ? '' : '-desktop'}`}>
          {t('common:offline.preparing_local')}
        </p>
      );
    }
    return (
      <>
        <p className={RENGLON} data-testid={`card-linea-${card.id}${sufijo}`}>{conteo}</p>
        <div className={RENGLON_DOS}>
          {temaCoincidente ? (
            <span className="truncate" data-testid={`card-tema-${card.id}${sufijo}`}>
              {t('dashboard:tarjeta_plegable.tema_coincide', { topic: temaCoincidente, count: cuantosTemas || 1 })}
            </span>
          ) : fecha ? (
            <span className="flex items-center gap-1.5 min-w-0" data-testid={`card-fecha-${card.id}${sufijo}`}>
              <Calendar className="w-3.5 h-3.5 shrink-0" aria-hidden="true" />
              <span className="truncate">{t(llaveFecha, { date: fecha })}</span>
            </span>
          ) : null}
        </div>
      </>
    );
  };
  const pie = (sufijo) => {
    if (caducada) return null;
    if (bajando || preparando) {
      return (
        <>
          <Medidor izquierda={(
            <ChipDeEstado tono="neutro" icono="giro" testid={`card-estado-${card.id}${sufijo}`}>{t('dashboard:doc_card.status.processing')}</ChipDeEstado>
          )} pct={PCT_BAJANDO} valor={`${PCT_BAJANDO}%`} color={COLOR_BARRA_PROCESANDO} />
          {preparando ? (
            <button type="button" onClick={(e) => { e.stopPropagation(); if (alReintentar) alReintentar(card); }}
              className={BOTON_PIE_SECUNDARIO} data-testid={`retry-hint-card-${card.id}${sufijo}`}>
              {t('dashboard:doc_card.tap_to_retry')}
            </button>
          ) : null}
        </>
      );
    }
    return (
      <>
        <MedidorDePreparacion valor={preparacion} t={t} sufijoId={`card-${card.id}${sufijo}`} />
        <button type="button"
          onClick={(e) => { e.stopPropagation(); volverA.current = e.currentTarget; setTemasAbiertos(true); }}
          className={BOTON_PIE_SECUNDARIO} data-testid={`card-ver-temas-${card.id}${sufijo}`}>
          <ListChecks className={ICONO_VER_TEMAS} aria-hidden="true" />
          {t('dashboard:partes_padre.ver_temas')}
        </button>
      </>
    );
  };
  const tarjeta = (sufijo) => (
    <div onClick={picar} data-testid={`card-${card.id}${sufijo}`}>
      <CascaronDeTarjeta id={`card-${card.id}`} sufijo={sufijo} estado={estado} interna atenuada={caducada}
        icono={caducada
          ? <Lock className={TRAZO_ICONO_INTERNA} role="img" aria-label={t('dashboard:grupos_alumno.aria_caducada')} />
          : <FileText className={TRAZO_ICONO_INTERNA} aria-hidden="true" />}
        titulo={nombreConCortes(card.nombre)} tituloTitle={card.nombre}
        detalle={detalle(sufijo)} pie={pie(sufijo)}
        className={caducada ? '' : CURSOR_DE_LISTA} />
    </div>
  );

  return (
    <div>
      {conMovil && <div className={gemela ? '' : 'md:hidden'}>{tarjeta('-mobile')}</div>}
      {conEscritorio && <div className={gemela ? '' : 'hidden md:block'}>{tarjeta('')}</div>}
      {temasAbiertos && (
        <TemasDeLaParteModal open onClose={() => setTemasAbiertos(false)}
          documento={{ id: card.id, filename: card.nombre }} temasFijos={temasFijos} t={t} volverA={volverA} />
      )}
    </div>
  );
}
